import {
  IMenu,
  IMenuEntry,
  IProduct,
} from 'featureSliced/shared/store/reducers/CategoriesReducer/types'
import { IMenuItemsBucket } from 'featureSliced/shared/store/reducers/QRMenuBucketReducer/types'
import { IDiscount } from 'featureSliced/shared/store/reducers/SpecialsReducer/types'
import { getProductDiscount } from 'featureSliced/shared/utils/getProductDiscount'
import { getDiscountedPrice } from 'featureSliced/shared/utils/getDiscountedPrice'
import {
  getPrecision,
  roundValueTo,
} from 'featureSliced/shared/utils/roundValueTo'

export type FixedTemplateItem = {
  item: IMenuEntry
  menuIndex: number
}

/** every article the template attaches on its own, one entry per group — the
 * SmartCheckout adds a fixed article once per group too, so the app must not
 * collapse an article configured in several of them */
export const collectFixedItems = (menus?: IMenu[]): FixedTemplateItem[] =>
  (menus || []).flatMap((menu, menuIndex) =>
    (menu?.items || [])
      .filter((item) => item.isFixed)
      .map((item) => ({ item, menuIndex }))
  )

export const getFixedProductIds = (menus?: IMenu[]): Set<number> =>
  new Set(collectFixedItems(menus).map(({ item }) => item.id))

/**
 * A step the guest has no decision left in: either the fixed items already fill
 * the group's maximum, or every selectable item is an article the template
 * already attaches as fixed. `max: 0` means "no limit", so it can never be
 * filled — such a group is pointless only under the second rule.
 */
export const stepHasNothingToChoose = (
  menu: IMenu,
  fixedIds: Set<number>
): boolean => {
  const items = menu?.items || []
  if (!items.length) return true
  const max = Number(menu?.max) || 0
  if (max > 0 && items.filter((item) => item.isFixed).length >= max) return true
  return items.every((item) => item.isFixed || fixedIds.has(item.id))
}

/** indexes of the groups worth showing, in order */
export const getChoosableStepIndexes = (menus?: IMenu[]): number[] => {
  const all = menus || []
  const fixedIds = getFixedProductIds(all)
  return all
    .map((_, index) => index)
    .filter((index) => !stepHasNothingToChoose(all[index], fixedIds))
}

/** false = nothing in the template needs the guest, so no configuration step */
export const templateNeedsCompose = (product?: IProduct): boolean =>
  getChoosableStepIndexes(product?.menus).length > 0

/** the bucket-line shape for the template's fixed components — the one shape both
 * the picker and the single-step modal must produce, or the order, the Beleg and
 * the printouts see two different lines for the same product */
export const buildFixedBucketItems = (
  menus: IMenu[] | undefined,
  discounts: IDiscount[],
  currency: string
): IMenuItemsBucket[] =>
  collectFixedItems(menus).map(({ item, menuIndex }) => {
    const discountP = getProductDiscount(item as any, discounts)
    const finalPrice = item.price ?? 0
    return {
      productId: item.id,
      count: item.count ?? 1,
      menuIndex,
      price: finalPrice,
      name: item.title,
      kitchenInfo: item.kitchenInfo ?? [],
      imageUrl: item.imageUrl ?? undefined,
      categoryId: item.categoryId,
      isApplyDiscount: Boolean(discountP),
      isFixed: true,
      total: roundValueTo(
        getDiscountedPrice(finalPrice, discountP),
        getPrecision(currency)
      ),
    } as unknown as IMenuItemsBucket
  })
